<template>
  <div class="cover-wrapper">
    <!-- Desktop: Kiri -->
    <section class="cover-left" :style="leftBgStyle">
      <div class="left-content">
        <p class="subtitle">The Wedding of</p>
        <h1 class="couple-name-script">{{ orderedCoupleNames }}</h1>
        <p class="date">{{ weddingDateLong }}</p>
      </div>
    </section>

    <!-- Kanan (mobile: full width) -->
    <section class="cover-right">
      <audio ref="bgMusic" loop>
        <source :src="musicSrc" type="audio/mpeg">
      </audio>

      <!-- FAB -->
      <div class="fab-container" v-show="isFabVisible">
        <div class="fab-submenu">
          <button v-for="(btn, i) in fabSubButtons" :key="i"
            class="fab-btn fab-sub-btn"
            :style="fabSubStyles[i]"
            @click="scrollToSection(btn.section)">
            <span v-html="btn.svg"></span>
          </button>
        </div>
        <button class="fab-btn fab-music-btn" @click="toggleMusic">
          <svg v-if="!isMusicPlaying" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle>
          </svg>
          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect>
          </svg>
        </button>
        <button class="fab-btn fab-home-btn" @click="toggleFabMenu">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
        </button>
      </div>

      <!-- Cover Overlay (amplop) -->
      <CoverSection :is-open="isInvitationOpen" :guest-name="guestName" @open="openInvitation" />

      <!-- Main scrollable content -->
      <div id="mainContent" ref="mainContent" class="main-content" :class="{ 'is-opened': isInvitationOpen }">
        <BrideGroomSection ref="brideGroom" :is-invitation-open="isInvitationOpen" />
        <CountdownSection v-if="config.countdown !== 0" />
        <AkadResepsiSection v-if="config.maps_section !== 0" />
        <OurStorySection v-if="config.story_section !== 0" />
        <GallerySection v-if="config.gallery_section !== 0" />
        <WeddingGiftSection v-if="config.gift_section !== 0" />
        <WishesSection />
        <FooterSection />
      </div>
    </section>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useHead } from '@unhead/vue';
import { mapState } from 'pinia';
import { useInvitationStore } from '@/store';
import moment from 'moment';
import 'moment/locale/id';

import CoverSection from '@/components/CoverSection.vue';
import BrideGroomSection from '@/components/BrideGroomSection.vue';
import CountdownSection from '@/components/CountdownSection.vue';
import AkadResepsiSection from '@/components/AkadResepsiSection.vue';
import OurStorySection from '@/components/OurStorySection.vue';
import GallerySection from '@/components/GallerySection.vue';
import WeddingGiftSection from '@/components/WeddingGiftSection.vue';
import WishesSection from '@/components/WishesSection.vue';
import FooterSection from '@/components/FooterSection.vue';

export default {
  name: 'HomePage',

  components: {
    CoverSection, BrideGroomSection, CountdownSection, AkadResepsiSection,
    OurStorySection, GallerySection, WeddingGiftSection, WishesSection, FooterSection,
  },

  setup() {
    const store = useInvitationStore();

    const orderedCoupleNames = computed(() => {
      const p = store.client.nama_pria || '';
      const w = store.client.nama_wanita || '';
      if (!p && !w) return 'Mempelai';
      return store.client.hajat === 'wanita' ? `${w} & ${p}` : `${p} & ${w}`;
    });

    useHead(computed(() => {
      const cover = store.client.foto_cover
        ? `https://admin.itsyourdayofficial.com/foto_cover/${store.client.foto_cover}`
        : '';
      const title = `Wedding of ${orderedCoupleNames.value}`;
      const desc = `Mohon doa dan restunya atas pernikahan kami ${orderedCoupleNames.value}`;
      const ogImage = cover 
        ? `https://images.weserv.nl/?url=${encodeURIComponent(cover.replace(/^https?:\/\//, ''))}&w=1200&h=630&fit=cover&a=center&output=jpg&q=85` 
        : 'https://itsyourdayofficial.com/og/default-1200x630.jpg';

      return {
        title,
        link: [
          { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
          { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
          { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Great+Vibes&family=Tangerine&family=Cinzel:wght@400;500;600&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&display=swap' },
          ...(cover ? [
            { rel: 'icon', href: cover },
            { rel: 'preload', as: 'image', href: cover }
          ] : []),
        ],
        meta: [
          { property: 'og:title', content: title },
          { property: 'og:description', content: desc },
          { property: 'description', content: desc },
          { property: 'og:image', content: ogImage },
          { property: 'og:type', content: 'website' },
          { property: 'og:url', content: typeof window !== 'undefined' ? window.location.href : '' },
        ],
      };
    }));

    return {
      store,
      config: computed(() => store.config || {}),
      orderedCoupleNames
    };
  },

  // Called server-side by main.server.js before renderToString
  async asyncData({ $api, route, store }) {
    const invStore = useInvitationStore(store);
    const slug = route.params.slug;

    let toParam = route.query.to || '';
    if (toParam) {
      const decoded = decodeURIComponent(toParam);
      const validPattern = /^[A-Za-z0-9\u00C0-\u017E\s]+$/;
      toParam = validPattern.test(decoded) ? decoded : '';
    }

    const templateUrl = typeof window !== 'undefined'
      ? window.location.origin + '/'
      : 'https://classic-jawa.itsyourdayofficial.com/';

    try {
      const response = await $api.get(`/${slug}`, {
        params: { to: toParam, template: templateUrl },
      });
      const data = response?.data?.data;
      if (!data) return;
      invStore.setData(data);
      invStore.guest = toParam || null;
      invStore.fallbackName = toParam ? '' : 'Tamu Undangan';
    } catch (e) {
      console.error('asyncData error:', e.message);
    }
  },

  data() {
    return {
      isInvitationOpen: false,
      isMusicPlaying: false,
      isFabVisible: false,
      fabMenuOpen: false,
      // Orbit config — matches FAB in mockup-cover
      fabOrbit: { R: 145, cx: 1.5, cy: -29, startDeg: -85, endDeg: 5, stagger: 45 },
      fabSubStyles: Array(5).fill(null).map(() => ({
        transform: 'translate(0, 0) scale(0.5)',
        opacity: '0',
        pointerEvents: 'none',
        transitionDelay: '0ms',
      })),
      fabSubButtons: [
        { section: 'section3', svg: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>' },
        { section: 'section5', svg: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>' },
        { section: 'section9', svg: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>' },
        { section: 'section8', svg: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>' },
        { section: 'section7', svg: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>' },
      ],
    };
  },

  computed: {
    ...mapState(useInvitationStore, ['client', 'config']),
    guestName() {
      const store = useInvitationStore();
      return store.guest || this.$route?.query?.to || '';
    },
    weddingDateLong() {
      const d = this.client.tgl_resepsi || this.client.tgl_akad;
      return d ? moment(d).locale('id').format('dddd, DD MMMM YYYY') : '';
    },
    leftBgStyle() {
      const url = this.client.foto_cover
        ? `https://admin.itsyourdayofficial.com/foto_cover/${this.client.foto_cover}`
        : 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1200&q=80';
      return { backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.4)), url(${url}), url('/img/bg_section1 copy.jpeg')`, backgroundSize: 'cover', backgroundPosition: 'center' };
    },
    musicSrc() {
      return this.client?.song
        ? `https://admin.itsyourdayofficial.com/song/${this.client.song}`
        : 'https://www.w3schools.com/html/horse.mp3';
    },
  },

  watch: {
    musicSrc(newSrc) {
      if (!newSrc) return;
      const audio = this.$refs.bgMusic;
      if (audio) {
        audio.load(); // Must reload after <source> src changes
      }
    },
  },

  mounted() {
    // Client-side fallback if store is empty (direct navigation without SSR)
    if (!this.client?.id && !this.client?.nama_pria) {
      this.fetchClientData();
    }
  },

  methods: {
    async fetchClientData() {
      const slug = this.$route?.params?.slug;
      if (!slug) return;

      let toParam = this.$route?.query?.to || '';
      if (toParam) {
        const decoded = decodeURIComponent(toParam);
        const validPattern = /^[A-Za-z0-9\u00C0-\u017E\s]+$/;
        toParam = validPattern.test(decoded) ? decoded : '';
      }

      try {
        const response = await this.$api.get(`/${slug}`, {
          params: { to: toParam, template: window.location.origin + '/' },
        });
        const data = response?.data?.data;
        if (!data) { this.$router.push({ name: 'NotFoundPage' }); return; }
        const store = useInvitationStore();
        store.setData(data);
        store.guest = toParam || null;
      } catch (e) {
        console.error('fetchClientData error:', e.message);
      }
    },

    openInvitation() {
      this.isInvitationOpen = true;
      this.isFabVisible = true;
      if (!this.isMusicPlaying) this.toggleMusic();
      this.$refs.brideGroom?.startVideo?.();
      this.$nextTick(() => {
        if (this.$refs.mainContent) this.$refs.mainContent.scrollTop = 0;
      });
    },

    toggleMusic() {
      const audio = this.$refs.bgMusic;
      if (!audio) return;
      if (this.isMusicPlaying) {
        audio.pause();
      } else {
        audio.play().catch(() => {});
      }
      this.isMusicPlaying = !this.isMusicPlaying;
    },

    toggleFabMenu() {
      if (this.fabMenuOpen) {
        this.closeFabMenu();
      } else {
        this.openFabMenu();
      }
    },

    orbitPositions(n) {
      const { R, cx, cy, startDeg, endDeg } = this.fabOrbit;
      const positions = [];
      for (let i = 0; i < n; i++) {
        const deg = startDeg + i * (endDeg - startDeg) / (n - 1);
        const rad = deg * Math.PI / 180;
        positions.push({ tx: cx + R * Math.cos(rad), ty: cy + R * Math.sin(rad) });
      }
      return positions;
    },

    openFabMenu() {
      this.fabMenuOpen = true;
      const positions = this.orbitPositions(this.fabSubButtons.length);
      this.fabSubStyles = positions.map((pos, i) => ({
        transform: `translate(${pos.tx.toFixed(1)}px, ${pos.ty.toFixed(1)}px) scale(1)`,
        opacity: '1',
        pointerEvents: 'auto',
        transitionDelay: `${i * this.fabOrbit.stagger}ms`,
        transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease',
      }));
    },

    closeFabMenu() {
      this.fabMenuOpen = false;
      const n = this.fabSubButtons.length;
      this.fabSubStyles = this.fabSubStyles.map((_, i) => ({
        transform: 'translate(0, 0) scale(0.5)',
        opacity: '0',
        pointerEvents: 'none',
        transitionDelay: `${(n - 1 - i) * this.fabOrbit.stagger}ms`,
        transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease',
      }));
    },

    scrollToSection(sectionId) {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      this.closeFabMenu();
    },
  },
};
</script>

<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
</style>

<style scoped>
.cover-wrapper {
  display: flex; width: 100vw; height: 100dvh;
  font-family: 'Cormorant Garamond', serif; background-color: #f4f6f8; color: #465B73; overflow: hidden;
}

/* Left panel (desktop only) */
.cover-left {
  display: none; flex: 5.5; background-size: cover; background-position: center;
  position: relative; color: white;
}
.left-content {
  position: absolute; bottom: 12%; width: 100%; text-align: center;
  text-shadow: 1px 1px 6px rgba(0,0,0,0.4);
}
.left-content .subtitle { font-family: 'Cormorant Garamond', serif; font-size: 1.3rem; letter-spacing: 1.5px; }
.left-content .couple-name-script { font-family: 'Great Vibes', cursive; font-size: 6rem; font-weight: 400; margin: 0.5rem 0; line-height: 1; }
.left-content .date { font-size: 1.1rem; letter-spacing: 1px; }

/* Right panel */
.cover-right {
  height: 100dvh; width: calc(100dvh * 11 / 16); max-width: 100vw; margin: 0 auto;
  overflow: hidden; position: relative; background-color: #E2E8EC; z-index: 1;
}
.cover-right::-webkit-scrollbar { display: none; }

/* Main scrollable content */
.main-content {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  overflow-y: auto; overflow-x: hidden; scroll-behavior: smooth;
  opacity: 0; transition: opacity 1s ease; transition-delay: 0.3s; z-index: 10;
}
.main-content.is-opened { opacity: 1; }
.main-content::-webkit-scrollbar { display: none; }

/* FAB container */
.fab-container {
  position: absolute; left: 15px;
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  z-index: 100; width: 45px; height: 45px;
}
.fab-submenu { position: absolute; bottom: 0; left: 0; }
.fab-sub-btn {
  position: absolute; bottom: 0; left: 0; width: 42px; height: 42px;
  transform: translate(0, 0) scale(0.5); opacity: 0; pointer-events: none;
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease;
}
.fab-home-btn { position: absolute; bottom: 0; left: 0; }
.fab-music-btn { position: absolute; bottom: 55px; left: 0; }
.fab-btn {
  width: 45px; height: 45px; border-radius: 50%; background-color: #3b3b3b; color: white;
  border: 2px solid white; cursor: pointer; display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 8px rgba(0,0,0,0.25); transition: background-color 0.3s;
}
.fab-btn:hover { background-color: #2b2b2b; }
.fab-sub-btn { width: 42px; height: 42px; }

@media (min-width: 768px) {
  .cover-left { display: block; flex: 1; }
  .cover-right { width: calc(100vh * 9 / 16); }
}
</style>
